const LAST_WORKSPACE_UNAME_KEY = 'ditto:lastWorkspaceUname';
const LAST_APP_LOCATION_KEY = 'ditto:lastAppLocation';

type StoredAppLocation = {
  pathname: string;
  search: string;
};

function parseStoredLocation(raw: string): StoredAppLocation | null {
  try {
    const parsed = JSON.parse(raw) as StoredAppLocation;
    if (
      typeof parsed.pathname !== 'string' ||
      typeof parsed.search !== 'string'
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function workspaceUnameFromPath(pathname: string): string | undefined {
  const match = /^\/workspace\/([^/]+)/.exec(pathname);
  return match?.[1];
}

function isRestorableWorkspacePath(
  pathname: string,
  search: string,
  workspaces: { uname: string }[]
): boolean {
  const uname = workspaceUnameFromPath(pathname);
  if (!uname || !workspaces.some(w => w.uname === uname)) {
    return false;
  }
  if (!pathname.startsWith('/workspace/')) {
    return false;
  }
  // Only in-app locations (pathname + search); no hash to avoid accidental token-like state
  if (search.includes('://')) {
    return false;
  }
  return true;
}

/**
 * Remember which workspace the user was in so `/` and browser back
 * (e.g. from back office) can return there instead of always using `workspaces[0]`.
 */
export function setLastVisitedWorkspaceUname(uname: string): void {
  try {
    sessionStorage.setItem(LAST_WORKSPACE_UNAME_KEY, uname);
  } catch {
    // Storage unavailable (private mode, quota) — ignore
  }
}

/**
 * Remember the exact app URL inside a workspace (path + query) for restore after admin, etc.
 */
export function setLastAppLocation(pathname: string, search: string): void {
  try {
    const payload: StoredAppLocation = { pathname, search };
    sessionStorage.setItem(LAST_APP_LOCATION_KEY, JSON.stringify(payload));
  } catch {
    // Storage unavailable — ignore
  }
}

export function getLastVisitedWorkspaceUname(
  workspaces: { uname: string }[] | undefined
): string | undefined {
  const path = getRestoredAppPath(workspaces);
  if (path) {
    return workspaceUnameFromPath(path.split('?')[0] ?? '');
  }

  if (!workspaces?.length) {
    return undefined;
  }
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(LAST_WORKSPACE_UNAME_KEY);
  } catch {
    return undefined;
  }
  if (!stored) {
    return undefined;
  }
  return workspaces.some(w => w.uname === stored) ? stored : undefined;
}

/**
 * Full path to restore (e.g. `/workspace/acme/regulatory-watch/audit-table?foo=1`), or undefined.
 */
export function getRestoredAppPath(
  workspaces: { uname: string }[] | undefined
): string | undefined {
  if (!workspaces?.length) {
    return undefined;
  }
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(LAST_APP_LOCATION_KEY);
  } catch {
    return undefined;
  }
  if (!raw) {
    return undefined;
  }
  const parsed = parseStoredLocation(raw);
  if (!parsed) {
    return undefined;
  }
  if (!isRestorableWorkspacePath(parsed.pathname, parsed.search, workspaces)) {
    return undefined;
  }
  return `${parsed.pathname}${parsed.search}`;
}
